<template>
  <dd-modal
    v-model:visible="internalShowModal"
    :title="title"
    :subtitle="subtitle"
    class="mailbox-verification-popup-email-viewer"
  >
    <loading-placeholder v-if="pollingForVerification" :lines="10" />
    <div v-else>
      <template v-if="hasVerificationEmails">
        <div v-for="(convo, index) in recentCustomerConvos" :key="index" class="verification-email-success">
          <h4>{{ convo.title}}</h4>
          <!-- HTML -->
          <div v-if="convo.stylizedHTMLVersionLink" class="html-description-wrapper">
            <iframe
              :src="convo.stylizedHTMLVersionLink"
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              frameborder="0"
              scrolling="auto" 
            />
          </div>
          <span v-else v-html="convo.description" />
        </div>
      </template>
      <div v-else class="verification-email-failure">
        <p>
          We didn't find a new email in this mailbox. Try these tips:
        </p>
        <p><br></p>
        <ul>
          <li><a href="#" class="just-a-link" @click.stop.prevent="pollForVerificationEmail()">Check again</a>. Sometimes
            the verification email may take a minute to get here.</li>
          <li><a href="#" class="just-a-link" @click.stop.prevent="internalShowModal = false">Close this window</a>
            and review our auto-forwarding instructions again.</li>
          <li>At a loss? <a href="https://www.donedone.com/help-center" target="_blank" class="just-a-link">Reach out to us</a> so we can help.</li>
        </ul>
      </div>
    </div>
    <template #footer>
      <span v-if="verificationEmailNotFound">
        <dd-button type="text" @click="internalShowModal = false">Close window</dd-button>
        <dd-button
          type="flat"
          @click="pollForVerificationEmail()"
        >
          Check for email again
        </dd-button>
      </span>
      <span v-else-if="!pollingForVerification && hasVerificationEmails">
        <dd-button
          type="text"
          @click="pollForVerificationEmail()"
        >
          I don't see the verification email. Check again.
        </dd-button>
        <dd-button
          :color="ColorAssistant.MailboxColor"
          type="flat"
          @click="internalShowModal = false"
        >
          Done
        </dd-button>
      </span>
    </template>
  </dd-modal>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { IConvoForForwardingVerification } from '@/api/types/response.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'

export default defineComponent({

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:showModal'],

  data() {
    return {
      ColorAssistant,
      pollingForVerification: true,
      hasVerificationEmails: false,
      poller: null as any,
      pollCount: 0,
      pollIntervalInMS: 5000,
      pollMax: 12,
      recentCustomerConvos: [] as IConvoForForwardingVerification[],
    }
  },
  head: {
    title: 'Verify auto-forwarding address',
  },

  computed: {
    internalShowModal: {
      get(): boolean {
        return this.showModal
      },
      set(value: any): any {
        this.$emit('update:showModal', value)
      },
    },
    mailboxID(): number {
      return Number(this.$route.params.id)
    },
    title(): string {
      if (this.pollingForVerification) {
        return 'Searching for an email...'
      } else if (this.hasVerificationEmails) {
        if (this.recentCustomerConvos.length > 1) {
          return `We found ${this.recentCustomerConvos.length} recent emails!`
        }
        return 'Email found!'
      } else {
        return 'No email found'
      }
    },
    subtitle(): string {
      if (this.pollingForVerification) {
        return ''
      } else if (this.hasVerificationEmails) {
        if (this.recentCustomerConvos.length > 1) {
          return 'Find the one about auto-forwarding and follow the instructions.'
        }
        return 'Follow the instructions below before closing the window.'
      }
      return ''
    },
    verificationEmailNotFound(): boolean {
      return !this.hasVerificationEmails && !this.pollingForVerification
    },
  },

  watch: {
    pollCount: {
      handler(newValue) {
        if (newValue >= this.pollMax) {
          this.endPolling()
        }
      },
    },
  },

  beforeUnmount() {
    this.endPolling()
  },

  async created() {
    this.pollForVerificationEmail()
  },

  methods: {
    endPolling() {
      clearInterval(this.poller)
      this.pollingForVerification = false
    },
    pollForVerificationEmail() {
      this.hasVerificationEmails = false
      this.pollingForVerification = true
      this.pollCount = 0
      this.poller = setInterval(async () => {
        this.pollCount++
        await this.getMostRecentCustomerCreatedConvos()
      }, this.pollIntervalInMS)
    },
    async getMostRecentCustomerCreatedConvos() {
      try {
        this.recentCustomerConvos = await MailboxesAPI.get_MostRecentCustomerCreatedConvoForForwardingVerifications(
          this.mailboxID,
        )
        this.hasVerificationEmails = true
        this.endPolling()
      } catch (err) {
        console.log('No verification email found.')
      }
    },
  },
})
</script>

<style lang="scss">
.mailbox-verification-popup-email-viewer {
  .verification-email-success {
    padding: 20px;
    border-bottom: $border--solid-light;
    h4 {
      margin-bottom: 20px;
    }
    > span > * {
      @include lettering--p-sm;
      overflow-wrap: break-word;
    }
  }

  .verification-email-failure {
    display: flex;
    flex-direction: column;
  }

  .html-description-wrapper {
    iframe {
      width: 100%;
      border: solid 1px $silver-two;
      border-radius: 4px;
      min-height: 300px;
    }
  }
}
</style>
